import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Alert,
  Skeleton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  RHFTextField,
  RHFSelect,
} from '../hook-form';
import Logo from '../logo';
import axios from '../../utils/Axios';
import { formatDuration } from '../../utils/formatTime';

export default function CreateServiceStep({ onNext, onBack, isLoading, userServices, setUserServices, isLoadingServices }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Pass showCreateForm state to parent via onNext function
  const handleNext = () => {
    onNext(showCreateForm);
  };

  // Show existing services if user has any
  if (!showCreateForm && userServices && userServices.length > 0) {
    return (
      <Box sx={{ py: 2 }}>
        
        <Typography variant="h4" sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}>
          Your Services
        </Typography>
        
        <Alert severity="success" sx={{ mb: 3 }}>
          Great! You already have {userServices.length} service{userServices.length > 1 ? 's' : ''} created. 
          You can proceed to the next step or create additional services.
        </Alert>
        
        <Box sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          {userServices.map((service) => (
            <Box 
              key={service._id} 
              sx={{ 
                p: 2, 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1, 
                mb: 1,
                backgroundColor: 'background.paper'
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {service.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Duration: {formatDuration(service.duration)} • 
                    Category: {service.category?.name || 'Uncategorized'} • 
                    Status: <span style={{ color: service.hidden ? '#FF5630' : '#36B37E' }}>
                      {service.hidden ? 'Inactive' : 'Active'}
                    </span>
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Box>
        
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
          <Button variant="outlined" onClick={onBack} sx={{ minWidth: 120 }}>
            Back
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => setShowCreateForm(true)}
            sx={{ minWidth: 160 }}
          >
            Create Another Service
          </Button>
          <Button variant="contained" onClick={handleNext} sx={{ minWidth: 120 }}>
            Next
          </Button>
        </Stack>
      </Box>
    );
  }

  // Show loading skeleton while checking services
  if (isLoadingServices) {
    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <Logo width="200px" />
        </Box>
        
        <Typography variant="h4" sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}>
          Checking your services...
        </Typography>
        
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={60} />
        </Box>
      </Box>
    );
  }

  // Show create form if no services exist or user clicked "Create Another Service"
  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
        <Logo width="200px" />
      </Box>
      
      <Typography variant="h4" sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}>
        {showCreateForm ? 'Create Another Service' : "Let's create your first service"}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
        You can change service details and add more services later on. 
      </Typography>

      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={8}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Service Name *
            </Typography>
            <RHFTextField 
              name="serviceName"
              placeholder="e.g., 30-minute consultation"
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Duration *
            </Typography>
            <Stack direction="row" spacing={1}>
              <RHFTextField 
                name="serviceDuration"
                type="number"
                defaultValue={30}
                size="small"
                sx={{ width: 80 }}
              />
              <RHFSelect name="serviceDurationType" native size="small" sx={{ width: 140 }}>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
              </RHFSelect>
            </Stack>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
          {showCreateForm ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setShowCreateForm(false)}
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                onClick={handleNext}
                loading={isLoading}
                sx={{ minWidth: 120 }}
              >
                {isLoading ? 'Creating...' : 'Create Service'}
              </LoadingButton>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={onBack}
                sx={{ minWidth: 120 }}
              >
                Back
              </Button>
              <LoadingButton
                variant="contained"
                onClick={handleNext}
                loading={isLoading}
                sx={{ minWidth: 120 }}
              >
                {isLoading ? 'Creating...' : 'Next'}
              </LoadingButton>
            </>
          )}</Stack>
      </Box>
    </Box>
  );
}